// src/utils/internacional.js
// Funciones centrales para internacionalización.
// Usar SIEMPRE estas funciones en vez de "$" hardcodeado, "wa.me/57", etc.

import { getPais, PAIS_DEFAULT } from "./paises";

/**
 * Devuelve la configuración del país del tenant.
 * Si el tenant no tiene configuración guardada, usa Colombia por defecto
 * (esto garantiza que tenants existentes como Emmita sigan funcionando).
 */
export function getConfigTenant(tenant) {
  const codigo = tenant?.configuracion?.pais || PAIS_DEFAULT;
  return getPais(codigo);
}

/**
 * Formatea un monto en la moneda del tenant.
 * Ejemplos:
 *   Colombia (COP, 0 decimales): formatearMoneda(1500000, tenant) → "$1.500.000"
 *   Perú (PEN, 2 decimales):     formatearMoneda(1500.5, tenant)  → "S/ 1,500.50"
 *   USD:                          formatearMoneda(1500, tenant)    → "US$1,500.00"
 */
export function formatearMoneda(monto, tenant) {
  const cfg = getConfigTenant(tenant);
  const valor = Number(monto) || 0;
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: "currency",
      currency: cfg.moneda,
      minimumFractionDigits: cfg.decimales,
      maximumFractionDigits: cfg.decimales,
    }).format(valor);
  } catch {
    // Failsafe por si el locale o moneda no son soportados por el navegador
    return `$${valor.toLocaleString("es-CO")}`;
  }
}

/**
 * Limpia un teléfono dejando solo dígitos.
 */
function soloDigitos(telefono) {
  return String(telefono || "").replace(/\D/g, "");
}

/**
 * Antepone el indicativo del país solo si el número no lo tiene ya.
 */
function conIndicativo(telefono, indicativo) {
  const raw = String(telefono || "").trim();
  const limpio = soloDigitos(telefono);
  if (!limpio) return "";
  // Si el número se escribió con "+" (ej. "+52 55..."), ya es internacional:
  // se respeta tal cual sin anteponer el indicativo del tenant.
  if (raw.startsWith("+")) return limpio;
  return limpio.startsWith(indicativo) ? limpio : indicativo + limpio;
}

/**
 * Genera link de WhatsApp con el indicativo del país del tenant.
 * Ej: linkWhatsApp("3214909600", tenantColombia) → "https://wa.me/573214909600"
 */
export function linkWhatsApp(telefono, tenant) {
  const cfg = getConfigTenant(tenant);
  const numero = conIndicativo(telefono, cfg.indicativo);
  return numero ? `https://wa.me/${numero}` : "";
}

/**
 * Genera link tel: para llamadas (con + y código de país).
 * Ej: linkLlamada("3214909600", tenantColombia) → "tel:+573214909600"
 */
export function linkLlamada(telefono, tenant) {
  const cfg = getConfigTenant(tenant);
  const numero = conIndicativo(telefono, cfg.indicativo);
  return numero ? `tel:+${numero}` : "";
}

/**
 * Genera link de Google Maps incluyendo ciudad/país para mayor precisión.
 * Ej: linkMaps("Calle 5 #10-20", tenantColombia) → busca incluyendo "Colombia"
 */
export function linkMaps(direccion, tenant, ciudadExtra = "") {
  const texto = String(direccion || "").trim();
  // Si ya es un enlace http/https pegado por el usuario (Google Maps u otro),
  // se usa tal cual → lleva al pin exacto (fincas sin dirección, ubicaciones precisas).
  if (/^https?:\/\//i.test(texto)) return texto;
  if (!texto) return "";
  const cfg = getConfigTenant(tenant);
  const partes = [texto, ciudadExtra, cfg.nombre].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partes)}`;
}

/**
 * Devuelve el símbolo de moneda solo (para campos donde se quiere mostrar el símbolo separado del monto).
 * Ej: simboloMoneda(tenantColombia) → "$", simboloMoneda(tenantPeru) → "S/"
 */
export function simboloMoneda(tenant) {
  const cfg = getConfigTenant(tenant);
  try {
    const partes = new Intl.NumberFormat(cfg.locale, {
      style: "currency",
      currency: cfg.moneda,
    }).formatToParts(0);
    const sim = partes.find(p => p.type === "currency");
    return sim ? sim.value : "$";
  } catch {
    return "$";
  }
}

// ═══════════════════════════════════════════════════════════
// FECHAS DE CALENDARIO
// ═══════════════════════════════════════════════════════════
// Devuelve una fecha en formato AAAA-MM-DD.
//
// ⚠️ NUNCA usar new Date().toISOString().slice(0,10) para esto: toISOString
// entrega el día en UTC, así que en Colombia (UTC-5) desde las 7 p.m. ya
// devuelve el día siguiente. Un abono registrado a las 8 p.m. del 30 quedaba
// guardado como del 31.
//
// 🌍 Pendiente de i18n: cuando se configure la zona horaria por país del
// tenant, este es el ÚNICO archivo que hay que cambiar. Los 12 sitios que
// usan estas funciones quedan corregidos solos.
export function fechaLocal(fecha = new Date()) {
  if (!fecha) return "";
  // Si ya viene como texto AAAA-MM-DD, es una fecha de calendario y se
  // devuelve tal cual. Convertirla la leería como UTC y restaría un día.
  if (typeof fecha === "string") {
    const m = fecha.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${y}-${mes}-${dia}`;
}

// La fecha de hoy, según la hora del dispositivo.
export function fechaHoy() {
  return fechaLocal(new Date());
}

// Quita tildes y pasa a minusculas, para comparar textos sin que las tildes
// estorben. Asi "María", "MARIA" y "maria" cuentan como iguales.
export function normalizarTexto(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}